import { api } from '@/lib/api';

const SCRIPT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';
let _loading = null;

export const loadRazorpay = () => {
  if (window.Razorpay) return Promise.resolve(true);
  if (_loading) return _loading;
  _loading = new Promise((resolve, reject) => {
    // Reuse an existing tag if one is already in the DOM (e.g. from preload).
    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    const s = existing || document.createElement('script');
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      _loading = null;
      reject(new Error('Razorpay is taking too long to load — check your internet connection and try again'));
    }, 20000);
    const onOk = () => { if (settled) return; settled = true; clearTimeout(timer); resolve(true); };
    const onFail = () => { if (settled) return; settled = true; clearTimeout(timer); _loading = null; reject(new Error('Could not load Razorpay checkout script')); };
    s.addEventListener('load', onOk);
    s.addEventListener('error', onFail);
    if (window.Razorpay) { onOk(); return; }
    if (!existing) {
      s.src = SCRIPT_SRC;
      s.async = true;
      document.body.appendChild(s);
    }
  });
  return _loading;
};

// Fire-and-forget preload so the checkout opens instantly when the user taps Pay
// (avoids the "stuck on Opening…" feel on slower mobile connections).
export const preloadRazorpay = () => { loadRazorpay().catch(() => {}); };

/**
 * Create a Razorpay order via backend, open the checkout modal, then verify on success.
 * @param {{ purpose?: string, amount?: number, sponsor_id?: string, leg?: string, parental_id?: string, full_name?: string, phone?: string, prefill?: object, theme?: object, onSuccess?: function, onError?: function, onDismiss?: function }} opts
 */
export const startRazorpayCheckout = async (opts = {}) => {
  try {
    await loadRazorpay();
  } catch (err) {
    throw new Error(err.message || 'Razorpay SDK load failed — check internet / ad-blocker');
  }
  let order;
  try {
    const res = await api.post(opts.createPath || '/payments/razorpay/create-order', opts.orderPayload || {
      purpose: opts.purpose || 'website_yearly',
      amount: opts.amount,
      sponsor_id: opts.sponsor_id,
      leg: opts.leg,
      parental_id: opts.parental_id,
      full_name: opts.full_name,
      phone: opts.phone,
    });
    order = res.data;
  } catch (e) {
    const detail = e.response?.data?.detail || e.message || 'create_order_failed';
    throw new Error(`Could not create order — ${detail}`);
  }
  if (!order.key_id) throw new Error('Razorpay not configured on server (missing key_id)');
  if (!order.order_id) throw new Error('Server did not return an order_id');
  // Sanitize contact: Razorpay requires a clean 10-digit Indian number, otherwise
  // the "Enter mobile number" step throws a validation error mid-flow.
  const sanitizeContact = (raw) => {
    if (!raw) return undefined;
    const digits = String(raw).replace(/\D/g, '');
    if (!digits) return undefined;
    // Strip 91 / 091 prefix and keep last 10 digits
    const last10 = digits.slice(-10);
    return last10.length === 10 ? last10 : undefined;
  };
  const prefillContact = sanitizeContact(opts.prefill?.contact ?? opts.phone ?? order.phone);
  const prefillEmail = (opts.prefill?.email || order.email || '').trim() || undefined;
  const prefillName = (opts.prefill?.name || order.name || '').trim() || undefined;
  // HARD GATE: If we still don't have a 10-digit mobile, Razorpay's Contact-details
  // step shows up and on some Android/Chrome combos the user CANNOT tap the input.
  // Block the launch with a clear error instead of letting them get stuck.
  if (!prefillContact) {
    throw new Error('Please add your 10-digit mobile number in your Profile before paying. (We pre-fill it to Razorpay so the checkout opens directly on payment options.)');
  }
  return new Promise((resolve, reject) => {
    let rzp;
    try {
      rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency || 'INR',
        order_id: order.order_id,
        name: 'INDIVALIFE',
        description: opts.purpose === 'renewal' ? 'INDIVALIFE renewal' : 'INDIVALIFE website purchase',
        image: '/indivalife-logo.png',
        prefill: {
          ...(prefillName ? { name: prefillName } : {}),
          ...(prefillEmail ? { email: prefillEmail } : {}),
          ...(prefillContact ? { contact: prefillContact } : {}),
        },
        theme: opts.theme || { color: '#1D4ED8' },
        modal: {
          ondismiss: () => {
            if (opts.onDismiss) opts.onDismiss();
            resolve({ dismissed: true });
          },
          escape: true,
        },
        handler: async (response) => {
          try {
            const { data } = await api.post(opts.verifyPath || '/payments/razorpay/verify', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            if (opts.onSuccess) opts.onSuccess(data);
            resolve(data);
          } catch (err) {
            const m = err.response?.data?.detail || err.message || 'verification_failed';
            if (opts.onError) opts.onError(new Error(m));
            reject(new Error(`Payment verify failed — ${m}`));
          }
        },
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[Razorpay] constructor failed', err);
      return reject(new Error('Could not initialise Razorpay — domain may not be whitelisted'));
    }
    rzp.on('payment.failed', (resp) => {
      const err = resp.error || resp;
      // eslint-disable-next-line no-console
      console.error('[Razorpay] payment.failed', err);
      const m = err?.description || err?.reason || err?.message || 'payment_failed';
      if (opts.onError) opts.onError(err);
      reject(new Error(`Payment failed — ${m}`));
    });
    try {
      rzp.open();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[Razorpay] open() failed', err);
      reject(new Error('Could not open Razorpay checkout — verify domain is whitelisted in Razorpay dashboard'));
    }
  });
};
